'use client';

import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useKindLabel } from '../_kindLabel';
import { CloseIcon } from './icons';
import { MicroFilterChips, usePopupItem } from './FilterChips';

/**
 * Shared chrome elements used by every popup renderer: kind badge,
 * close button, loading + error states, and the page padding token.
 */

export function KindBadge({ kind }: { kind: string }) {
  const kindLabel = useKindLabel();
  const palette = ((): { fg: string; bg: string } => {
    switch (kind) {
      case 'SHORT_VIDEO':
        return {
          fg: 'var(--color-success, #14b8a6)',
          bg: 'rgba(20,184,166,0.12)',
        };
      case 'SHORTS':
        return { fg: '#ec4899', bg: 'rgba(236,72,153,0.12)' };
      case 'QUIZ':
        return {
          fg: 'var(--color-warning, #f97316)',
          bg: 'rgba(249,115,22,0.12)',
        };
      case 'WEEKLY_READ':
        return {
          fg: 'var(--color-success, #16a34a)',
          bg: 'rgba(22,163,74,0.12)',
        };
      case 'INTERACTIVE':
        return {
          fg: 'var(--color-brand-500, #7c3aed)',
          bg: 'rgba(124,58,237,0.12)',
        };
      case 'INFO_CARDS':
        return {
          fg: 'var(--color-warning, #f97316)',
          bg: 'rgba(249,115,22,0.12)',
        };
      case 'CHECKLIST':
        return {
          fg: 'var(--color-success, #16a34a)',
          bg: 'rgba(22,163,74,0.12)',
        };
      case 'AUDIO':
        return { fg: '#0ea5e9', bg: 'rgba(14,165,233,0.12)' };
      case 'DOWNLOAD':
        return {
          fg: '#64748b',
          bg: 'rgba(100,116,139,0.12)',
        };
      case 'SCENARIO':
        return {
          fg: '#7c3aed',
          bg: 'rgba(124,58,237,0.12)',
        };
      case 'LEARNING_PATH':
        return {
          fg: '#0d9488',
          bg: 'rgba(13,148,136,0.12)',
        };
      case 'POLL':
        return { fg: '#7c3aed', bg: 'rgba(124,58,237,0.12)' };
      case 'BUSINESS_STORY_CARDS':
        return {
          fg: '#6366f1',
          bg: 'rgba(99,102,241,0.12)',
        };
      default:
        return {
          fg: 'var(--color-text-primary, #18181b)',
          bg: 'var(--color-surface-page, #f4f4f5)',
        };
    }
  })();
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        // A flex-column parent (PopupHeader) would stretch the chip to the
        // full row — a badge hugs its label.
        alignSelf: 'flex-start',
        padding: 'var(--space-1, 4px) var(--space-2-5, 10px)',
        borderRadius: 999,
        fontSize: '11px',
        fontWeight: 600,
        letterSpacing: 'var(--font-tracking-wider, 0.04em)',
        textTransform: 'uppercase',
        color: palette.fg,
        background: palette.bg,
      }}
    >
      {kindLabel(kind)}
    </span>
  );
}

export function CloseButton({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('site-builder-blocks');
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label={t('microChrome.close')}
      style={{
        position: 'absolute',
        top: 12,
        right: 12,
        width: 36,
        height: 36,
        borderRadius: '50%',
        border: 'none',
        background: 'rgba(0,0,0,0.55)',
        color: 'var(--color-text-on-brand, #fff)',
        cursor: 'pointer',
        display: 'grid',
        placeItems: 'center',
        zIndex: 2,
      }}
    >
      <CloseIcon />
    </button>
  );
}

export function LoadingState() {
  const { t } = useTranslation('site-builder-blocks');
  return (
    <div
      style={{
        ...padded,
        textAlign: 'center',
        padding: 48,
        color: 'var(--color-text-secondary, #71717a)',
      }}
    >
      {t('microChrome.loading')}
    </div>
  );
}

export function ErrorState({ message }: { message?: string }) {
  const { t } = useTranslation('site-builder-blocks');
  return (
    <div
      style={{ ...padded, textAlign: 'center', padding: 48, color: 'var(--color-danger, #dc2626)' }}
    >
      {message || t('microChrome.loadFailed')}
    </div>
  );
}

export const padded: CSSProperties = {
  padding: 'clamp(20px, 4vw, 32px)',
};

/** Deck navigation buttons (card stacks: previous / next / finish). */
export const btnPrimary: CSSProperties = {
  padding: 'var(--space-2, 8px) var(--space-4, 16px)',
  border: 'none',
  borderRadius: 8,
  background: 'var(--color-text-primary, #18181b)',
  color: 'var(--color-text-on-brand, #fff)',
  fontWeight: 600,
  fontSize: '13px',
  cursor: 'pointer',
};

export const btnSecondary: CSSProperties = {
  padding: 'var(--space-2, 8px) var(--space-4, 16px)',
  border: '1px solid var(--color-border, rgba(0,0,0,0.12))',
  borderRadius: 8,
  background: 'transparent',
  color: 'var(--color-text-primary, #18181b)',
  fontWeight: 500,
  fontSize: '13px',
  cursor: 'pointer',
};

/** Section wrapper used inside renderers for chrome above the player. */
export function PopupHeader({
  kind,
  title,
  description,
  meta,
}: {
  kind: string;
  title: string;
  description?: string | null;
  meta?: ReactNode;
}) {
  const popupItem = usePopupItem();
  return (
    <div style={{ ...padded, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <KindBadge kind={kind} />
      <h2
        style={{
          margin: 0,
          fontSize: 'var(--font-size-xl, 22px)',
          fontWeight: 600,
          lineHeight: 1.2,
        }}
      >
        {title}
      </h2>
      {description && (
        <p
          style={{
            margin: 0,
            fontSize: 'var(--font-size-sm, 14px)',
            color: 'var(--color-text-secondary, #71717a)',
            lineHeight: 1.55,
          }}
        >
          {description}
        </p>
      )}
      {meta && <div>{meta}</div>}
      {/* Category / tag filters for the item this popup shows — same
          affordance as the short-video info card, on every kind. */}
      {popupItem && (
        <MicroFilterChips
          item={popupItem.item}
          onClose={popupItem.onClose}
          style={{ marginTop: 4 }}
        />
      )}
    </div>
  );
}

/**
 * Heart toggle shown next to {@link CloseButton} in the player popup —
 * micro has no detail route, so the popup is where a learner favorites
 * an item (the flag lives on their progress row). Optimistic: flips
 * immediately, reverts if the write fails.
 */
export function FavoriteButton({
  favorite,
  onToggle,
}: {
  favorite: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation('site-builder-blocks');
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={favorite ? t('microChrome.favorite.remove') : t('microChrome.favorite.add')}
      aria-pressed={favorite}
      title={favorite ? t('microChrome.favorite.remove') : t('microChrome.favorite.add')}
      style={{
        position: 'absolute',
        top: 12,
        right: 56,
        width: 36,
        height: 36,
        borderRadius: '50%',
        border: '1px solid rgba(255,255,255,0.6)',
        background: favorite ? 'var(--color-danger, #ef4444)' : 'rgba(255,255,255,0.95)',
        color: favorite ? '#fff' : 'var(--color-danger, #ef4444)',
        cursor: 'pointer',
        display: 'grid',
        placeItems: 'center',
        zIndex: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
        transition: 'transform 120ms ease, background 160ms ease, color 160ms ease',
        transform: favorite ? 'scale(1.06)' : 'scale(1)',
      }}
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill={favorite ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      </svg>
    </button>
  );
}
